import { Text } from '@chakra-ui/react';

import { LEAccordionItem } from '../../components/LEAccordion';
import { useLEAccordionItemContext } from '../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import { ItemStatusType } from '../../components/LEAccordion/utils';
import PrimaryButton from '../../components/ui/buttons/PrimaryButton';
import {
  LienholderRequirement,
  useTemporaryInfoSsnUpdateMutation,
  useTemporaryInfoUpdateMutation,
} from '../../gql/generated/graphql';
import { useStep } from '../../hooks';
import { useTempInfo } from '../../hooks/useTempInfo';
import PayoffIntelligenceMissingFields, {
  PayoffMissingFieldFormValues,
} from '../LoanApp/components/PayoffIntelligenceMissingFields';

type PayoffIntelligenceMissingFieldsAccordionItemProps = {
  initialStatus?: ItemStatusType;
  missingFields: LienholderRequirement[];
};

const PayoffIntelligenceMissingFieldsAccordionItemBody = ({
  missingFields,
}: {
  missingFields: LienholderRequirement[];
}) => {
  const { updateState, isExpanded } = useLEAccordionItemContext();
  const step = useStep();
  const { info, infoLoading } = useTempInfo({ fetchPolicy: 'network-only', refresh: isExpanded });
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const [temporaryInfoSsnUpdate] = useTemporaryInfoSsnUpdateMutation();

  const tempInfoData = info?.data;
  if (infoLoading || !tempInfoData) {
    return null;
  }

  const handleSubmit = async (values: PayoffMissingFieldFormValues) => {
    if (!info?.id) {
      return;
    }

    updateState({ status: 'loading' });

    const { ssn, ...rest } = values;

    if (ssn && typeof ssn === 'string') {
      await temporaryInfoSsnUpdate({
        variables: {
          tempInfoId: info.id,
          ssn,
          isCobuyer: false,
        },
      });
    }

    await updateTemporaryInfo({
      variables: {
        info: {
          id: info.id,
          data: {
            ...rest,
            state: rest.state as string | undefined,
            zip: rest.zip as string | undefined,
          },
        },
      },
    });

    updateState({ status: 'complete' });
    step.moveNext();
  };

  return (
    <PayoffIntelligenceMissingFields
      missingFields={missingFields}
      onSubmit={handleSubmit}
      submitButton={({ isSubmitting }) => (
        <PrimaryButton loading={isSubmitting} type="submit">
          NEXT
        </PrimaryButton>
      )}
    />
  );
};

const PayoffIntelligenceMissingFieldsAccordionItem = ({
  initialStatus,
  missingFields,
}: PayoffIntelligenceMissingFieldsAccordionItemProps) => {
  return (
    <LEAccordionItem
      accordionKey="payoffMissingFields"
      title="HELP US FIND YOUR PAYOFF"
      initialStatus={initialStatus}
    >
      <Text textAlign="center">
        We can retrieve your payoff directly from your lienholder for you. Please provide the
        information below so we can locate your account.
      </Text>
      <PayoffIntelligenceMissingFieldsAccordionItemBody missingFields={missingFields} />
    </LEAccordionItem>
  );
};

export default PayoffIntelligenceMissingFieldsAccordionItem;
